/**
 * Regras e textos da linha da aba "Meus Estudos" (Flux Personalizado, #1991).
 *
 * Funções puras sobre o domínio (`MyStudy`): a linha (#1998) e a coluna de ações (#1994) só
 * exibem o que sai daqui. A habilitação das ações preserva a regra da `Row` atual
 * (`Flux/Personalized/Row/index.tsx`), com os textos do protótipo (.dc.html:484-490, 636-647).
 */

import { FluxStudyStatusCode } from 'constants/fluxStudyStatus';
import {
  formatDateOnly, formatInstantFull, formatInstantRelative, instantToEpochMs,
} from 'helper/flux/studyDates';
import { ListedStudy } from 'helper/studies';
import { Instant, MyStudy, MyStudyHorizon } from 'types/Flux/Personalized/myStudies';

/** Quem está vendo a linha. */
export interface StudyViewer {
  userId: number;
  isAdmin: boolean;
}

/**
 * Há usuário logado? O `selectUserId` devolve -1 sem JWT: sem usuário válido, a tela não grava
 * a URL nem a sessão dos filtros, não mostra o aviso de migração e não consulta a lista.
 *
 * @param userId - Id do usuário (`selectUserId`).
 * @returns `true` para inteiro seguro > 0.
 */
export function isValidUserId(userId: number): boolean {
  return Number.isSafeInteger(userId) && userId > 0;
}

/** Estado de uma ação: habilitada ou não, com a dica que explica o porquê. */
export interface StudyActionState {
  enabled: boolean;
  hint: string;
}

export interface StudyActionsState {
  resend: StudyActionState;
  download: StudyActionState;
  cancel: StudyActionState;
  remove: StudyActionState;
}

/** Linha de execução: rótulo, valor relativo e o instante completo para o `title`. */
export interface StudyExecutionLine {
  label: 'Última execução' | 'Na fila desde' | 'Ainda não executado';
  /** Ex.: 'hoje, 14:20'; null em "Ainda não executado". */
  when: string | null;
  /** Ex.: '07/10/2026 14:20'; null em "Ainda não executado". */
  title: string | null;
}

/** Só os campos que os hooks legados de ação leem (download, cancelar e excluir). */
export type StudyActionRef = Pick<ListedStudy, 'id' | 'ds_nome_estudo' | 'dt_last_response' | 'ck_internal_error'>;

/** Status finais, os mesmos de `FINISHED_STUDY_STATUSES` (helper/studies.ts). */
const FINISHED_STATUSES: readonly FluxStudyStatusCode[] = ['pronto', 'erro', 'cancelado'];

const formatCount = (count: number) => count.toLocaleString('pt-BR');

const pluralize = (count: number, singular: string, plural: string) => (
  `${formatCount(count)} ${count === 1 ? singular : plural}`
);

/** Status final. Código desconhecido não é final, como o rótulo desconhecido no legado. */
function isFinished(study: MyStudy): boolean {
  return study.status !== null && FINISHED_STATUSES.includes(study.status);
}

/**
 * Pego pelo worker e ainda não finalizado: a mesma regra de `isStudyRunning` e do backend,
 * que recusa excluir nesse estado.
 */
function isRunning(study: MyStudy): boolean {
  return study.lastExecution !== null && !isFinished(study);
}

/**
 * Horizonte do estudo.
 *
 * @returns '08/10/2026 – 31/01/2027'; o fim nulo (Q7) sai como '—'.
 */
export function formatHorizon(horizon: MyStudyHorizon): string {
  const end = horizon.end === null ? '—' : formatDateOnly(horizon.end);
  return `${formatDateOnly(horizon.start)} – ${end}`;
}

/**
 * Composição do estudo.
 *
 * @returns '1 cenário · 1 bloco', '2 cenários · 6 blocos'.
 */
export function formatComposition(study: Pick<MyStudy, 'scenarioCount' | 'blockCount'>): string {
  return `${pluralize(study.scenarioCount, 'cenário', 'cenários')} · ${pluralize(study.blockCount, 'bloco', 'blocos')}`;
}

function executionLine(
  label: StudyExecutionLine['label'],
  instant: Instant,
  nowMs: number,
): StudyExecutionLine {
  return { label, when: formatInstantRelative(instant, nowMs), title: formatInstantFull(instant) };
}

/**
 * Linha "Última execução" da coluna de status (D9).
 *
 * Em Fila mostra "Na fila desde" com `COALESCE(ultima_execucao, enviado_em)`, a mesma data que
 * filtra e ordena a lista: `ultima_execucao` de um estudo em fila é a hora em que o worker o
 * reservou, não uma execução, e serve para o "Na fila desde" (Q6). Nos demais status, a última
 * execução e, sem ela, o envio. Sem nenhuma das datas, "Ainda não executado".
 *
 * @param nowMs - Agora, em milissegundos desde a época, lido a cada render.
 */
export function formatExecutionLine(study: MyStudy, nowMs: number): StudyExecutionLine {
  if (study.status === 'em_fila') {
    const queuedAt = study.lastExecution ?? study.sentAt;
    if (queuedAt !== null) return executionLine('Na fila desde', queuedAt, nowMs);
  } else if (study.lastExecution !== null) {
    return executionLine('Última execução', study.lastExecution, nowMs);
  } else if (study.sentAt !== null) {
    return executionLine('Na fila desde', study.sentAt, nowMs);
  }
  return { label: 'Ainda não executado', when: null, title: null };
}

/**
 * Linha de resposta, com a contagem de execuções (D26-3). `executionCount` é aproximado e só
 * aparece aqui, nunca em regra.
 *
 * @returns 'Resposta hoje, 08:58 · 1 execução', 'Em execução · 1 execução',
 *   'Aguardando na fila · 0 execuções' ou 'Sem resposta · 2 execuções'.
 */
export function formatResponseLine(study: MyStudy, nowMs: number): string {
  const count = pluralize(study.executionCount, 'execução', 'execuções');
  if (study.lastResponse !== null) {
    return `Resposta ${formatInstantRelative(study.lastResponse, nowMs)} · ${count}`;
  }
  if (study.status === 'executando') return `Em execução · ${count}`;
  if (study.status === 'em_fila') return `Aguardando na fila · ${count}`;
  return `Sem resposta · ${count}`;
}

/** Pode cancelar e excluir: admin ou quem salvou o estudo por último (`Row/index.tsx:32`). */
export function isStudyOwner(study: MyStudy, viewer: StudyViewer): boolean {
  return viewer.isAdmin || (study.creator !== null && study.creator.id === viewer.userId);
}

/** Foi o próprio usuário que salvou o estudo: marca "(você)", sem o atalho de admin. */
export function isCreatedByMe(study: MyStudy, userId: number): boolean {
  return study.creator !== null && study.creator.id === userId;
}

/**
 * O Baixar está habilitado? Com resposta e sem erro interno, a regra do `downloadState` (e do
 * `usePersonalizedDownload`). O aviso de "pronto para download" do polling (#2001) usa a mesma.
 *
 * @param study - A linha, ou só os dois campos que decidem.
 * @returns `true` quando há o que baixar.
 */
export function isStudyDownloadable(study: Pick<MyStudy, 'internalError' | 'lastResponse'>): boolean {
  return !study.internalError && study.lastResponse !== null;
}

function downloadState(study: MyStudy): StudyActionState {
  if (study.internalError) {
    return { enabled: false, hint: 'Indisponível: o estudo terminou com erro' };
  }
  if (study.lastResponse === null) {
    return {
      enabled: false,
      hint: study.status === 'erro'
        ? 'Indisponível: o estudo terminou com erro'
        : 'Indisponível: ainda não houve resposta',
    };
  }
  return { enabled: true, hint: 'Baixar resultados (ZIP)' };
}

function cancelState(study: MyStudy, owner: boolean): StudyActionState {
  if (!owner) return { enabled: false, hint: 'Só o autor ou um administrador pode cancelar' };
  if (isFinished(study)) return { enabled: false, hint: 'O estudo já terminou' };
  return { enabled: true, hint: 'Cancelar processamento' };
}

function removeState(study: MyStudy, owner: boolean): StudyActionState {
  if (!owner) return { enabled: false, hint: 'Só o autor ou um administrador pode excluir' };
  if (isRunning(study)) return { enabled: false, hint: 'Cancele o estudo antes de excluir' };
  return { enabled: true, hint: 'Excluir estudo' };
}

/**
 * Habilitação e dica das quatro ações da linha, preservando a regra atual (D2, D3):
 * - Reenviar: sempre;
 * - Baixar: com resposta e sem erro interno (`usePersonalizedDownload`);
 * - Cancelar: dono, com o estudo ainda não finalizado (`!isStudyFinished`);
 * - Excluir: dono, com o estudo fora de execução (`!isStudyRunning`): um Em Fila ainda não
 *   reservado pelo worker pode ser excluído.
 */
export function getStudyActionsState(study: MyStudy, viewer: StudyViewer): StudyActionsState {
  const owner = isStudyOwner(study, viewer);
  return {
    resend: { enabled: true, hint: 'Reenviar estudo' },
    download: downloadState(study),
    cancel: cancelState(study, owner),
    remove: removeState(study, owner),
  };
}

/**
 * Referência mínima para os hooks legados de download, cancelar e excluir, que leem só
 * `id`, `ds_nome_estudo`, `dt_last_response` (unix em segundos) e `ck_internal_error`.
 */
export function toStudyActionRef(study: MyStudy): StudyActionRef {
  const responseMs = study.lastResponse === null ? NaN : instantToEpochMs(study.lastResponse);
  return {
    id: study.id,
    ds_nome_estudo: study.name,
    // Ilegível conta como sem resposta: o Baixar fica bloqueado, como no legado.
    dt_last_response: Number.isNaN(responseMs) ? null : Math.floor(responseMs / 1000),
    ck_internal_error: study.internalError,
  };
}
